/**
 * AuthCard Component - Reusable Card for Authentication Flows
 *
 * Provides consistent card structure with:
 * - Gold accent bar at top
 * - Four corner accents (22px, 10px inset)
 * - Sharp corners (border-radius: 0)
 * - Responsive padding
 *
 * USAGE:
 * <AuthCard>
 *   <AuthCard.Icon>
 *     <SpriteIcon name="mail" size={28} />
 *   </AuthCard.Icon>
 *   <AuthCard.Heading>Your Heading</AuthCard.Heading>
 *   <AuthCard.Content>
 *     Your content here
 *   </AuthCard.Content>
 * </AuthCard>
 */

import React from 'react';
import '../../theme-components/50-auth-card.css';

export interface AuthCardProps {
  children: React.ReactNode;
  className?: string;
}

export interface AuthCardIconProps {
  children: React.ReactNode;
  className?: string;
}

export interface AuthCardHeadingProps {
  children: React.ReactNode;
  className?: string;
}

export interface AuthCardSubheadingProps {
  children: React.ReactNode;
  className?: string;
}

export interface AuthCardContentProps {
  children: React.ReactNode;
  className?: string;
}

const AuthCard: React.FC<AuthCardProps> & {
  Icon: React.FC<AuthCardIconProps>;
  Heading: React.FC<AuthCardHeadingProps>;
  Subheading: React.FC<AuthCardSubheadingProps>;
  Content: React.FC<AuthCardContentProps>;
} = ({ children, className = '' }) => {
  return (
    <div className={`auth-card ${className}`}>
      <div className="auth-card__accent-bar"></div>
      <div className="auth-card__inner">
        {children}
      </div>
    </div>
  );
};

// Icon container (gold/warm background with border)
const Icon: React.FC<AuthCardIconProps> = ({ children, className = '' }) => {
  return (
    <div className={`auth-card__logo ${className}`}>
      {children}
    </div>
  );
};

// Main heading
const Heading: React.FC<AuthCardHeadingProps> = ({ children, className = '' }) => {
  return (
    <h1 className={`auth-card__property-name ${className}`}>
      {children}
    </h1>
  );
};

// Subheading/subtitle
const Subheading: React.FC<AuthCardSubheadingProps> = ({ children, className = '' }) => {
  return (
    <div className={`auth-card__subtitle ${className}`}>
      {children}
    </div>
  );
};

// Content wrapper (no additional styling, just for semantic structure)
const Content: React.FC<AuthCardContentProps> = ({ children, className = '' }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
};

AuthCard.Icon = Icon;
AuthCard.Heading = Heading;
AuthCard.Subheading = Subheading;
AuthCard.Content = Content;

export default AuthCard;
